import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Check, Users, Zap, Loader2, ArrowRight, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";

const PLANS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    interval: "forever",
    features: ["1 learner", "25 stimulus cards", "Arrays + Sort activities", "5 AI card generations / mo"],
    cta: "Current plan",
    lookup_key: null,
  },
  {
    id: "solo_monthly",
    name: "Solo BCBA",
    price: "$19",
    interval: "per month",
    yearlyPrice: "$15",
    yearlyInterval: "per month, billed yearly",
    yearlyKey: "solo_yearly",
    features: [
      "Unlimited learners & cards",
      "Drag-to-match arrays (2–10 cards)",
      "Category sorting drills",
      "AI-powered Token Boards with themed reinforcers",
      "AI photo & illustration generation",
      "PDF exports + parent consent",
      "Concept auto-pairing",
    ],
    cta: "Activate — free during beta",
    lookup_key: "solo_monthly",
    highlighted: true,
  },
  {
    id: "clinic_monthly",
    name: "Clinic",
    price: "$79",
    interval: "per month · up to 10 seats",
    yearlyPrice: "$65",
    yearlyInterval: "per month, billed yearly · up to 10 seats",
    yearlyKey: "clinic_yearly",
    features: [
      "Everything in Solo",
      "Team sharing + roles",
      "Shared team-wide Token Board presets",
      "Bulk category editor",
      "Bulk illustration regen",
      "Audit log & compliance reports",
    ],
    cta: "Activate — free during beta",
    lookup_key: "clinic_monthly",
  },
];

export default function BillingPage() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const nav = useNavigate();
  const currentPlan = user?.plan || "free";

  // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh is stable from AuthContext; one-shot mount refresh only.
  useEffect(() => { refresh?.(); }, []);

  const redeem = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;
    setRedeeming(true);
    try {
      const r = await http.post("/beta/redeem", { code: inviteCode.trim().toUpperCase() });
      toast.success(`Beta unlocked — ${r.data.plan} plan free until ${new Date(r.data.beta_ends_at).toLocaleDateString()}`);
      setInviteCode("");
      await refresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid or already-used code");
    } finally {
      setRedeeming(false);
    }
  };

  const start = async (planKey) => {
    if (!planKey) return;
    // Beta mode — grant the plan directly, no credit card, no Stripe checkout.
    const tier = planKey.startsWith("solo") ? "solo" : "clinic";
    setBusy(planKey);
    try {
      const r = await http.post("/billing/beta-activate", { plan: tier });
      toast.success(`Beta activated — ${tier === "solo" ? "Solo BCBA" : "Clinic"} free until ${new Date(r.data.beta_ends_at).toLocaleDateString()}`);
      await refresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not activate beta");
    } finally {
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const r = await http.post("/payments/portal", {
        return_url: window.location.origin + "/billing",
      });
      window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not open billing portal");
      setBusy(null);
    }
  };

  return (
    <div className="p-4 sm:p-8 md:p-12 max-w-5xl">
      <PageHeader
        eyebrow="Billing"
        title="Choose your plan"
        description="Free during public beta — no credit card required. Cards & billing return once we exit beta; you'll be notified before then."
      />

      {user?.beta_ends_at && new Date(user.beta_ends_at) > new Date() && (
        <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="beta-banner">
          <Sparkles className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-sm text-emerald-900">
            <div className="font-bold">You're on beta access — Clinic plan free until {new Date(user.beta_ends_at).toLocaleDateString()}.</div>
            <div className="text-xs text-emerald-800 mt-0.5">Add a card any time from the Manage subscription button below to auto-continue when beta ends. Otherwise you'll drop to Free.</div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-3 mb-8">
        <button
          onClick={() => setYearly(false)}
          data-testid="billing-monthly-toggle"
          className={`px-4 h-9 rounded-full text-sm font-semibold border transition-colors ${!yearly ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
        >
          Monthly
        </button>
        <button
          onClick={() => setYearly(true)}
          data-testid="billing-yearly-toggle"
          className={`px-4 h-9 rounded-full text-sm font-semibold border transition-colors ${yearly ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
        >
          Yearly <span className="ml-1 text-xs text-emerald-500">save ~20%</span>
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {PLANS.map((p) => {
          const isCurrent =
            (p.id === "free" && currentPlan === "free") ||
            (p.lookup_key && (currentPlan === "solo" && p.id.startsWith("solo")) ||
              (currentPlan === "clinic" && p.id.startsWith("clinic")));
          const price = yearly && p.yearlyPrice ? p.yearlyPrice : p.price;
          const interval = yearly && p.yearlyInterval ? p.yearlyInterval : p.interval;
          const key = yearly && p.yearlyKey ? p.yearlyKey : p.lookup_key;
          return (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`rounded-2xl border p-6 flex flex-col ${
                p.highlighted
                  ? "bg-slate-900 text-white border-slate-900 shadow-xl -translate-y-2"
                  : "bg-white text-slate-900 border-slate-200"
              }`}
            >
              <div className="text-xs font-bold uppercase tracking-[0.14em] opacity-70 flex items-center gap-2">
                {p.id === "clinic_monthly" ? <Users className="w-3 h-3" /> :
                 p.id === "solo_monthly"   ? <Zap className="w-3 h-3" /> :
                                              <Sparkles className="w-3 h-3" />}
                {p.name}
              </div>
              <div className="mt-2">
                <span className="font-display text-4xl font-extrabold">{price}</span>
              </div>
              <div className={`text-xs mt-0.5 ${p.highlighted ? "text-slate-300" : "text-slate-500"}`}>{interval}</div>
              <ul className="mt-5 space-y-2 text-sm flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlighted ? "text-emerald-400" : "text-emerald-600"}`} />
                    <span className={p.highlighted ? "text-slate-100" : "text-slate-700"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => key ? start(key) : null}
                disabled={busy === key || !key || isCurrent}
                className={`mt-6 rounded-full h-11 ${p.highlighted ? "bg-white text-slate-900 hover:bg-slate-100" : ""}`}
                data-testid={`plan-${p.id}-cta`}
              >
                {isCurrent ? "Current plan" :
                 busy === key ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activating…</> :
                 <>{p.cta} <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          );
        })}
      </div>

      {currentPlan && currentPlan !== "free" && (
        <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700">Beta access — {currentPlan}</div>
            <div className="font-display text-lg font-bold mt-0.5 text-emerald-900">You're all set. No card needed during beta.</div>
            <div className="text-xs text-emerald-800 mt-1">We'll email you at least a week before beta ends so you can decide whether to add a card and keep the plan.</div>
          </div>
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5" data-testid="redeem-invite-section">
        <div className="flex items-center gap-2 mb-3">
          <Ticket className="w-4 h-4 text-slate-500" />
          <div className="font-display font-bold text-lg">Have an invite code?</div>
        </div>
        <p className="text-sm text-slate-500 mb-3">Beta invitees get 30 days of Clinic access on the house.</p>
        <form onSubmit={redeem} className="flex gap-2 max-w-md">
          <Input
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            placeholder="Enter your code"
            className="rounded-xl uppercase tracking-widest font-semibold"
            data-testid="redeem-invite-input"
            maxLength={20}
          />
          <Button type="submit" disabled={redeeming || !inviteCode.trim()} className="rounded-full" data-testid="redeem-invite-button">
            {redeeming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redeeming…</> : "Redeem"}
          </Button>
        </form>
      </div>

      <div className="mt-10 text-center">
        <button
          onClick={() => nav(-1)}
          className="text-sm text-slate-500 hover:text-slate-900"
          data-testid="billing-back"
        >
          ← Back
        </button>
      </div>

      <div className="mt-6 text-xs text-slate-400 text-center max-w-2xl mx-auto">
        Beta program — no payment collected. Standard pricing (shown above) applies once we exit beta; every user gets at least 7 days' notice before any charge.
      </div>
    </div>
  );
}
